import { ErrorRoute } from '@/routes/ErrorRoute'
import { RootState } from '@/store'
import { storeActions } from '@/store/actions'
import { useStoreDispatch } from '@/store/hooks'
import { ChatMessageSender } from '@repo/shared/dtos/message'
import { isEmptyString } from '@repo/shared/utils'
import { Button } from '@repo/ui/button'
import ChatDayMessages from '@repo/ui/chatDayMessages'
import ChatEmpty from '@repo/ui/chatEmpty'
import { DataProvider } from '@repo/ui/dataProvider'
import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
} from '@repo/ui/drawer'
import { useScrollToEnd, useT } from '@repo/ui/hooks'
import Message, { isNewThread } from '@repo/ui/message'
import MessageStreamed from '@repo/ui/messageStreamed'
import RichTextarea from '@repo/ui/richTextarea'
import { createMessagesByDay, sortByKey } from '@repo/ui/utils'
import {
  ArrowDownLeft,
  Loader2,
  MessageSquareX,
  MessagesSquare,
  Send,
} from 'lucide-react'
import { ReactNode, useCallback, useRef, useState } from 'react'

export default function Chat({
  modalHandler,
}: {
  modalHandler: { open: boolean; setOpen: (open: boolean) => void }
}) {
  const { t } = useT({ keyPrefix: 'beneficiary-app' })

  const dispatch = useStoreDispatch()
  return (
    <Drawer
      onOpenChange={modalHandler.setOpen}
      open={modalHandler.open}
      defaultOpen={modalHandler.open}
    >
      <DrawerContent className="max-h-full min-h-full flex-shrink-0 md:m-auto md:max-w-[400px]">
        <DrawerHeader className="relative flex-shrink-0">
          <div
            className="bg-primary-50 absolute -top-4 right-2 flex-shrink-0 rounded-full p-3"
            onClick={() => modalHandler.setOpen(false)}
          >
            <ArrowDownLeft className="size-5" />
          </div>
          <DrawerTitle>
            <div className="text-primary tall:text-base tall:h-fit flex h-0 items-center justify-center gap-2 text-sm leading-none">
              <MessagesSquare className="tall:h-6 tall:w-6 size-4" />
              {t('chatSession.title')}
            </div>
          </DrawerTitle>
        </DrawerHeader>
        <div className="max-w-screen border-primary-200 flex w-full flex-1 flex-col overflow-hidden border-t">
          <DataProvider
            selectors={[
              (state: RootState) => state.beneficiary.messages,
              (state: RootState) => state.beneficiary.organization,
            ]}
            errorComponent={<ErrorRoute message="Can't load chat messages." />}
            loadingComponent={
              <div className="flex min-h-full flex-1 items-center justify-center">
                <Loader2 className="animate-spin" />
              </div>
            }
            render={(messages, { chatSettings }) => {
              const sortedMessages = [...messages]
                .sort(sortByKey('sentAt'))
                .map((m) => ({
                  content: m.content,
                  createdAtTime: m.sentAt,
                  id: m.id,
                  isSender: m.sender !== ChatMessageSender.Bot,
                  isStreaming: m.streaming,
                }))
              const lastStreamingMessageId = sortedMessages
                .filter((m) => m.isStreaming)
                .slice(-1)[0]?.id
              const messagesByDay = createMessagesByDay(
                structuredClone(sortedMessages),
              )
              const days = [...messagesByDay.keys()]
              return (
                <div className="flex w-full flex-1 flex-col overflow-y-auto px-4 xl:px-6">
                  <ScrollableContainer
                    children={(scrollToLastMessage) => (
                      <>
                        {days.length > 0 && (
                          <Messages
                            displayFeedback={chatSettings?.allowFeedback}
                            onAskedForFeedback={(messageId) => {
                              dispatch(storeActions.openFeedback({ messageId }))
                            }}
                            days={days}
                            lastStreamingMessageId={lastStreamingMessageId}
                            messagesByDay={messagesByDay}
                            scrollToLastMessage={scrollToLastMessage}
                          />
                        )}

                        <DataProvider
                          selectors={[
                            (state: RootState) =>
                              state.beneficiary.isBotThinking,
                          ]}
                          render={(isBotThinking) => (
                            <>
                              {isBotThinking ? (
                                <Message
                                  className="animate-pulse font-black"
                                  content="..."
                                  createdAtTime={new Date().getTime()}
                                  isNewThread={true}
                                  isSender={false}
                                  onLifeCycle={{
                                    onMounted: scrollToLastMessage,
                                  }}
                                />
                              ) : (
                                days.length === 0 && <ChatEmpty />
                              )}
                            </>
                          )}
                        />
                      </>
                    )}
                  />
                </div>
              )
            }}
          />

          <MessageInput />
          <FeedbackForm />
        </div>
      </DrawerContent>
    </Drawer>
  )
}

function ScrollableContainer({
  children,
}: {
  children: (scrollToLastMessage: () => void) => ReactNode
}) {
  const containerRef = useRef<HTMLDivElement>(null)
  const scrollToLastMessage = useScrollToEnd(containerRef)
  return (
    <div ref={containerRef} className="flex w-full flex-col">
      {children(scrollToLastMessage)}
    </div>
  )
}

function Messages({
  days,
  lastStreamingMessageId,
  messagesByDay,
  scrollToLastMessage,
  onAskedForFeedback,
  displayFeedback,
}: {
  onAskedForFeedback: (messageId: string) => void
  days: string[]
  lastStreamingMessageId: string | undefined
  messagesByDay: Map<
    string,
    {
      content: string
      createdAtTime: number
      id: string
      isSender: boolean
      isStreaming: boolean
    }[]
  >
  scrollToLastMessage: () => void
  displayFeedback?: boolean
}) {
  return (
    <>
      {days.map((day, dayIndex) => (
        <ChatDayMessages day={day} key={day}>
          {messagesByDay.get(day)?.map((m, i) => {
            if (m.isStreaming && m.id === lastStreamingMessageId)
              return (
                <MessageStreamed
                  content={m.content}
                  createdAtTime={m.createdAtTime}
                  isNewThread={isNewThread(m, messagesByDay.get(day)!)}
                  isSender={m.isSender}
                  key={m.id}
                  onEndStream={scrollToLastMessage}
                  displayFeedback={displayFeedback}
                  onAskedForFeedback={() => onAskedForFeedback(m.id)}
                />
              )
            return (
              <Message
                displayFeedback={displayFeedback}
                onAskedForFeedback={() => onAskedForFeedback(m.id)}
                content={m.content}
                createdAtTime={m.createdAtTime}
                isNewThread={isNewThread(m, messagesByDay.get(day)!)}
                isSender={m.isSender}
                key={m.id}
                onLifeCycle={{
                  onMounted: () => {
                    if (
                      dayIndex === days.length - 1 &&
                      i === messagesByDay.get(day)!.length - 1
                    )
                      scrollToLastMessage()
                  },
                }}
              />
            )
          })}
        </ChatDayMessages>
      ))}
    </>
  )
}

function MessageInput() {
  const dispatch = useStoreDispatch()
  const { t, tc } = useT({ keyPrefix: 'beneficiary-app' })
  const [newMessage, setNewMessage] = useState<string>('')

  const clearChatSession = useCallback(async () => {
    await dispatch(storeActions.createChatSession())
  }, [])
  const sendMessage = useCallback((content: string) => {
    dispatch(storeActions.sendBeneficiaryMessage({ message: content }))
  }, [])
  return (
    <DataProvider
      selectors={[(state: RootState) => state.beneficiary.isBotThinking]}
      render={(isBotThinking) => (
        <div>
          <RichTextarea
            autoFocus={false}
            className="tall:min-h-28 mx-4 min-h-20 flex-shrink-0 xl:mx-6"
            onChange={setNewMessage}
            onSubmit={() => {
              if (isBotThinking || isEmptyString(newMessage)) return
              sendMessage(newMessage)
              setNewMessage('') // Reset
            }}
            placeholder={tc('write a message')}
            value={newMessage}
          />
          <div className="mx-4 flex justify-between xl:mx-6">
            <Button
              variant="link"
              size="sm"
              onClick={() => void clearChatSession()}
              className="ml-1 px-1 text-xs text-gray-400 hover:text-red-500"
            >
              <MessageSquareX className="mr-1 h-3 w-3" />
              {t('clearChat')}
            </Button>

            <Button
              variant="link"
              disabled={isEmptyString(newMessage) || isBotThinking}
              size="sm"
              onClick={() => {
                if (isEmptyString(newMessage)) return
                sendMessage(newMessage)
                setNewMessage('') // Reset
              }}
              className="text-primary-500"
            >
              <Send className="size-4 rotate-12" />
              {tc('send')}
            </Button>
          </div>
        </div>
      )}
    />
  )
}

function FeedbackForm() {
  const [feedbackState, setFeedbackState] = useState<{
    text: string
  }>({
    text: '',
  })
  const dispatch = useStoreDispatch()
  const { t } = useT({ keyPrefix: 'beneficiary-app.feedback' })

  function close() {
    setFeedbackState({
      text: '',
    })
    dispatch(storeActions.closeFeedback())
  }

  return (
    <DataProvider
      selectors={[(state: RootState) => state.feedback]}
      render={({ open, sendingStatus }) => (
        <Drawer
          // onOpenChange={() => {}}
          open={open}
          defaultOpen={open}
        >
          <DrawerContent className="max-h-full flex-shrink-0 md:m-auto md:max-w-[400px]">
            <div
              className="bg-primary-50 absolute right-2 top-2 flex-shrink-0 rounded-full p-3"
              onClick={() => {
                close()
              }}
            >
              <ArrowDownLeft className="size-5" />
            </div>

            <div className="flex flex-col p-8">
              {sendingStatus !== 'sent' && (
                <RichTextarea
                  autoFocus={false}
                  className="min-h-40 flex-shrink-0"
                  onChange={(text) => {
                    setFeedbackState({
                      ...feedbackState,
                      text,
                    })
                  }}
                  placeholder={t('placeholder')}
                  value={feedbackState.text}
                />
              )}

              {sendingStatus === 'idle' && (
                <Button
                  disabled={feedbackState.text.length < 5}
                  className="mt-4"
                  onClick={() => {
                    void dispatch(
                      storeActions.sendFeedback({
                        feedback: feedbackState.text,
                      }),
                    )
                  }}
                >
                  {t('send')}
                </Button>
              )}
              {sendingStatus === 'sending' && (
                <Button disabled className="mt-4">
                  {t('sending')}
                </Button>
              )}
              {sendingStatus === 'failed' && (
                <Button disabled className="mt-4">
                  {t('error')}
                </Button>
              )}
              {sendingStatus === 'sent' && (
                <>
                  <p className="m-8 text-center text-lg">{t('thanks')}</p>
                  <Button onClick={close}>{t('ok')}</Button>
                </>
              )}
            </div>
          </DrawerContent>
        </Drawer>
      )}
    />
  )
}
